import { useCallback, useEffect, useRef, useState } from "react";
import { getFloorStack, type FloorStackResponse, friendlyFloorStackError } from "../../lib/floorStackApi.js";

export type FloorStackQueryState =
  | { status: "loading" }
  | { status: "ready"; data: FloorStackResponse }
  | { status: "error"; message: string };

interface Options {
  /** Auto-refresh interval in ms. 0 disables polling. Default 30 000. */
  refreshIntervalMs?: number;
}

export function useFloorStack(siteId: string | null, tenantId: string | null, options: Options = {}) {
  const [state, setState] = useState<FloorStackQueryState>({ status: "loading" });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const refreshInterval = options.refreshIntervalMs ?? 30_000;

  const reload = useCallback(async () => {
    if (!siteId) return;
    try {
      const data = await getFloorStack(siteId, tenantId);
      setState({ status: "ready", data });
    } catch (err) {
      setState({ status: "error", message: friendlyFloorStackError(err) });
    }
  }, [siteId, tenantId]);

  useEffect(() => {
    setState({ status: "loading" });
    void reload();
    if (refreshInterval > 0 && siteId) {
      intervalRef.current = setInterval(() => { void reload(); }, refreshInterval);
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = null;
      };
    }
    return;
  }, [reload, refreshInterval, siteId]);

  return { state, reload };
}
